import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { 
  Bookmark, BookmarkCheck, Clock, MessageSquare, 
  Heart, ExternalLink, Trash2 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reaction icons
const REACTIONS = {
  support: { icon: '❤️', label: 'Support' },
  praying: { icon: '🙏', label: 'Praying' },
  celebrate: { icon: '👏', label: 'Celebrate' },
  insightful: { icon: '💎', label: 'Insightful' }
};

// Format relative time
const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

// Saved Post Card Component
const SavedPostCard = ({ post, onRemove }) => {
  const [expanded, setExpanded] = useState(false);
  const contentPreviewLength = 300;
  const needsExpand = post.content?.length > contentPreviewLength;
  
  // Get community name from ID
  const getCommunityName = (communityId) => {
    const communityNames = {
      'comm-ic-main': 'Inner Circle | Community Feed',
      'comm-ic-wins': 'Wins, Goals & Accountability',
      'comm-ic-prayer': 'Prayer & Encouragement',
      'comm-ic-relationships': 'Relationships, Boundaries & Discernment',
      'comm-ic-referrals': 'Trusted Referrals & Resources',
      'comm-ic-careers': 'Career Opportunities & Job Leads',
      'comm-ic-resume': 'Resume & LinkedIn Support',
      'comm-ic-content': 'Content Feedback & Visibility',
      'comm-inner-edge': 'Inner Edge Cohort',
      'comm-sdc': 'SDC Lounge',
      'comm-costa-rica': 'Costa Rica Retreat'
    };
    return communityNames[communityId] || 'Community';
  };
  
  return (
    <Card className="card-base overflow-hidden hover:shadow-md transition-shadow" data-testid={`saved-post-${post.id}`}>
      <CardContent className="p-0">
        {/* Post Header */}
        <div className="p-4 pb-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={post.author_image} />
                <AvatarFallback className="bg-forest text-white">
                  {post.author_name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-forest">{post.author_name}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Clock className="h-3 w-3" />
                  {formatTimeAgo(post.created_at)}
                  {post.support_type && (
                    <span className="px-2 py-0.5 bg-terracotta/10 text-terracotta rounded-full">
                      {post.support_type}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Link 
                to={`/communities/${post.community_id}`}
                className="text-xs px-2 py-1 bg-sand/50 text-forest/70 rounded-full hover:bg-sand transition-colors"
              >
                {getCommunityName(post.community_id)?.split('|')[0]?.trim()}
              </Link>
              <button 
                onClick={() => onRemove(post.id, post.community_id)}
                className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
                title="Remove from saved"
              >
                <Trash2 className="h-4 w-4 text-gray-400 group-hover:text-red-500" />
              </button>
            </div>
          </div>
          
          {/* Post Title */}
          {post.title && (
            <h3 className="font-heading text-lg font-semibold text-forest mt-3">
              {post.title}
            </h3>
          )}
        </div>
        
        {/* Post Content */}
        <div className="px-4 py-3">
          <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
            {expanded || !needsExpand 
              ? post.content 
              : `${post.content?.slice(0, contentPreviewLength)}...`}
          </p>
          {needsExpand && (
            <button 
              onClick={() => setExpanded(!expanded)}
              className="text-terracotta text-sm font-medium mt-2 hover:underline"
            >
              {expanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>
        
        {/* Post Media */}
        {post.media_urls && post.media_urls.length > 0 && (
          <div className="px-4 pb-3">
            <div className={`grid gap-2 ${post.media_urls.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {post.media_urls.slice(0, 2).map((url, idx) => (
                <div key={idx} className="rounded-lg overflow-hidden bg-sand/30">
                  {url.includes('.mp4') || url.includes('.mov') || url.includes('.webm') ? (
                    <video src={`${API_URL}${url}`} controls className="w-full max-h-48 object-contain" />
                  ) : (
                    <img 
                      src={url.startsWith('http') ? url : `${API_URL}${url}`} 
                      alt="Post media" 
                      className="w-full max-h-48 object-cover"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Footer */}
        <div className="px-4 py-3 border-t border-sand/50 flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            {/* Reactions Display */}
            {post.reactions && Object.entries(post.reactions).some(([_, count]) => count > 0) && (
              <div className="flex items-center gap-1">
                {Object.entries(post.reactions).map(([type, count]) => (
                  count > 0 && (
                    <span key={type} className="flex items-center">
                      {REACTIONS[type]?.icon}
                    </span>
                  )
                ))}
                <span className="ml-1">{post.total_reactions || 0}</span>
              </div>
            )}
            
            {/* Comment count */}
            {post.comment_count > 0 && (
              <span className="flex items-center gap-1">
                <MessageSquare className="h-4 w-4" />
                {post.comment_count}
              </span>
            )}
          </div>
          
          <Link 
            to={`/communities/${post.community_id}`}
            className="flex items-center gap-2 text-sm text-forest hover:text-terracotta transition-colors"
          >
            View in community
            <ExternalLink className="h-4 w-4" />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

const SavedPostsPage = () => {
  const [savedPosts, setSavedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const token = localStorage.getItem('token');
  
  const fetchSavedPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/bookmarks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch saved posts:', error);
      toast.error('Failed to load saved posts');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchSavedPosts();
  }, []);
  
  const handleRemoveBookmark = async (postId, communityId) => {
    try {
      await axios.post(
        `${API_URL}/api/communities/${communityId}/posts/${postId}/bookmark`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Removed from saved posts');
      setSavedPosts(savedPosts.filter(p => p.id !== postId));
    } catch (error) {
      toast.error('Failed to remove bookmark');
    }
  };
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8">
          <div className="animate-pulse">
            <div className="h-8 bg-sand rounded w-1/3 mb-6" />
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-40 bg-sand rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="saved-posts-page">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-terracotta/10 rounded-lg">
              <BookmarkCheck className="h-6 w-6 text-terracotta" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-forest">
              Saved Posts
            </h1>
          </div>
          <p className="text-gray-600 ml-12">
            Your personal library of wisdom and insights from the community.
          </p>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card className="card-base">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-forest/10 rounded-lg">
                <Bookmark className="h-5 w-5 text-forest" />
              </div>
              <div>
                <p className="text-2xl font-bold text-forest">{savedPosts.length}</p>
                <p className="text-sm text-gray-500">Saved posts</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card-base">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-terracotta/10 rounded-lg">
                <Heart className="h-5 w-5 text-terracotta" />
              </div>
              <div>
                <p className="text-2xl font-bold text-forest">
                  {savedPosts.reduce((acc, p) => acc + (p.total_reactions || 0), 0)}
                </p>
                <p className="text-sm text-gray-500">Total reactions</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card-base">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-sand rounded-lg">
                <MessageSquare className="h-5 w-5 text-forest" />
              </div>
              <div>
                <p className="text-2xl font-bold text-forest">
                  {savedPosts.reduce((acc, p) => acc + (p.comment_count || 0), 0)}
                </p>
                <p className="text-sm text-gray-500">Discussions</p>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Saved Posts List */}
        {savedPosts.length === 0 ? (
          <Card className="card-base">
            <CardContent className="p-12 text-center">
              <Bookmark className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl text-forest mb-2">No saved posts yet</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                When you find wisdom worth keeping, click the bookmark icon on any post to save it to your personal library.
              </p>
              <Link to="/communities">
                <Button className="bg-forest hover:bg-forest/90">
                  Explore Communities
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {savedPosts.map((post) => (
              <SavedPostCard
                key={post.id}
                post={post}
                onRemove={handleRemoveBookmark}
              />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SavedPostsPage;
